import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from 'react';
import Image from 'components/image';
import LoadingIndicator from 'components/loading-indicator';
import VideoSource from 'components/models/video-source';
import useToggle from 'hooks/use-toggle';
import useBreakpoints from 'hooks/use-breakpoints';
import videoSourceUtils from 'js/video-source-utils';
import PropTypes from 'prop-types';
import ReactPlayer from 'react-player';
import cn from 'classnames';

const Video = forwardRef(
  (
    {
      portraitSource,
      landscapeSource,
      className,
      imageSettings = {},
      fluid,
      playButtonTabIndex,
      lang = {
        playVideoButtonAriaLabel: 'Spill av video',
        videoAriaLabel: 'Video',
      },
    },
    ref
  ) => {
    const { isActive, activate } = useToggle(false);
    const [playing, setPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const playerContainerRef = useRef();
    const playerRef = useRef(null);
    const { isDesktop } = useBreakpoints(true);

    const onPlayBtnClick = e => {
      e.preventDefault();
      e.stopPropagation();

      if (isActive) {
        if (!isLoading) {
          setPlaying(true);
        }
      } else {
        setIsLoading(true);
        activate();
      }
    };

    const playVideo = () => {
      setPlaying(true);
      setIsLoading(false);
    };

    const onPlayerError = e => {
      // eslint-disable-next-line no-console
      console.log('onPlayerError: ', e);
      setIsLoading(false);
    };

    useImperativeHandle(ref, () => ({
      pause() {
        if (playing) {
          setPlaying(false);
        }
      },
    }));

    const { mobileVideo, desktopVideo } = useMemo(() => {
      return videoSourceUtils.getVideoSourceData(
        landscapeSource,
        portraitSource
      );
    }, [landscapeSource, portraitSource]);

    const video = isDesktop ? desktopVideo : mobileVideo;

    const { className: imageClassName, ...imageSettingsRest } = imageSettings;

    return video && video.data && video.data.src ? (
      <figure
        className={cn('video', {
          video_fluid: fluid,
          // Note: video.orientation property is ignored if fluid prop passed as true
          [`video_orientation-mobile-${mobileVideo.orientation}`]:
            mobileVideo.orientation && !fluid,
          [`video_orientation-desktop-${desktopVideo.orientation}`]:
            desktopVideo.orientation && !fluid,
          [className]: className,
        })}
      >
        {!playing && (
          <button
            className={cn('video__play-btn', {
              'video__play-btn_loading': isLoading,
            })}
            onClick={onPlayBtnClick}
            aria-label={lang.playVideoButtonAriaLabel}
            {...(playButtonTabIndex !== undefined && {
              tabIndex: playButtonTabIndex,
            })}
          >
            <span className="video__play-btn-circle">
              {isLoading && <LoadingIndicator className="video__loading" />}
            </span>
          </button>
        )}
        {video.data.previewImage && (
          <Image
            className={cn('video__image', {
              [imageClassName]: imageClassName,
              video__image_hidden: playing,
            })}
            {...video.data.previewImage}
            {...imageSettingsRest}
          />
        )}
        <div className="video__container" ref={playerContainerRef}>
          {isActive && (
            <ReactPlayer
              ref={playerRef}
              playing={playing}
              url={video.data.src}
              controls={true}
              playsinline={true}
              onReady={playVideo}
              onError={onPlayerError}
              config={{
                file: {
                  attributes: {
                    'aria-label': lang.videoAriaLabel,
                  },
                },
              }}
            />
          )}
        </div>
      </figure>
    ) : null;
  }
);

Video.propTypes = {
  portraitSource: PropTypes.exact(VideoSource.propTypes),
  landscapeSource: PropTypes.exact(VideoSource.propTypes),
  className: PropTypes.string,
  fluid: PropTypes.bool,
  imageSettings: PropTypes.shape({
    className: PropTypes.string,
    height: PropTypes.number,
    width: PropTypes.number,
    fluid: PropTypes.bool,
  }),
  playButtonTabIndex: PropTypes.string,
  lang: PropTypes.shape({
    playVideoButtonAriaLabel: PropTypes.string.isRequired,
    videoAriaLabel: PropTypes.string.isRequired,
  }).isRequired,
};

Video.viewModelMeta = {
  className: 'ignore',
  imageSettings: 'ignore',
  fluid: 'ignore',
  playButtonTabIndex: 'ignore',
};

export default Video;
